import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loanAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../utils/i18n';
import { ArrowLeft, Save, Send } from 'lucide-react';
import { useDialog } from '../../components/Dialog';

// Import step components
import Step1Upload from './components/Step1Upload';
import Step2Bank from './components/Step2Bank';
import Step3Amount from './components/Step3Amount';
import Step4Term from './components/Step4Term';
import Step5Dates from './components/Step5Dates';
import Step6Customer from './components/Step6Customer';
import Step7Notes from './components/Step7Notes';

import './LoanFormPage.css';

const LoanFormPage = () => {
  const navigate = useNavigate();
  const { language } = useAuth();
  const { t } = useTranslation(language);
  const { showWarning, showSuccess, showError, showConfirm, DialogComponent } = useDialog();

  const [currentStep, setCurrentStep] = useState(() => {
    const saved = sessionStorage.getItem('loanFormCurrentStep');
    return saved ? parseInt(saved) : 1;
  });

  const [formData, setFormData] = useState(() => {
    // Try to restore from sessionStorage
    const saved = sessionStorage.getItem('loanFormData');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved form data:', e);
      }
    }

    // Default initial state
    return {
      // Step 1
      documents: [],

      // Step 2
      bankId: '',
      branchId: '',

      // Step 3
      amount: 0,

      // Step 4
      termMonths: 12,

      // Step 5
      interestPaymentDate: 5,
      principalPaymentDate: 5,

      // Step 6
      customerId: null,
      customerName: '',
      customerPhone: '',
      customerEmail: '',
      customerIdNumber: '',
      customerAddress: '',

      // Step 7
      notes: '',
    };
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showValidation, setShowValidation] = useState(false);

  // Save formData to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('loanFormData', JSON.stringify(formData));
  }, [formData]);

  // Save currentStep to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('loanFormCurrentStep', currentStep.toString());
  }, [currentStep]);

  const steps = [
    { number: 1, title: t('uploadDocuments'), component: Step1Upload },
    { number: 2, title: t('selectBank'), component: Step2Bank },
    { number: 3, title: t('loanAmountStep'), component: Step3Amount },
    { number: 4, title: t('loanTermStep'), component: Step4Term },
    { number: 5, title: t('paymentDates'), component: Step5Dates },
    { number: 6, title: t('customerInfo'), component: Step6Customer },
    { number: 7, title: t('notesStep'), component: Step7Notes },
  ];

  // Auto-update currentStep based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const stepElements = steps.map(step => ({
        number: step.number,
        element: document.getElementById(`step-${step.number}`)
      }));

      // Find which step is currently in view
      for (let i = stepElements.length - 1; i >= 0; i--) {
        const { number, element } = stepElements[i];
        if (element) {
          const rect = element.getBoundingClientRect();
          // Check if step is in upper half of viewport
          if (rect.top <= window.innerHeight / 3) {
            setCurrentStep(number);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleStepClick = (stepNumber) => {
    setCurrentStep(stepNumber);
  };

  const CurrentStepComponent = steps[currentStep - 1].component;

  // Validate each step individually and return error messages
  const validateStep = (stepNumber) => {
    const stepErrors = {};

    switch (stepNumber) {
      case 1:
        if (!formData.documents || formData.documents.length === 0) {
          stepErrors.documents = t('pleaseUploadDocuments');
        }
        break;
      case 2:
        if (!formData.bankId) {
          stepErrors.bankId = t('pleaseSelectBank');
        }
        break;
      case 3:
        if (!formData.amount || formData.amount < 500000) {
          stepErrors.amount = t('minimumAmount500k');
        }
        break;
      case 4:
        if (!formData.termMonths || formData.termMonths <= 0) {
          stepErrors.termMonths = t('pleaseSelectTerm');
        }
        break;
      case 5:
        if (!formData.paymentDate && (!formData.interestPaymentDate || !formData.principalPaymentDate)) {
          stepErrors.paymentDate = t('pleaseSelectPaymentDate');
        }
        break;
      case 6:
        if (!formData.customerName) {
          stepErrors.customerName = t('customerNameRequired');
        }
        if (!formData.customerPhone) {
          stepErrors.customerPhone = t('customerPhoneRequired');
        }
        if (!formData.customerId) {
          if (!formData.customerCountry) {
            stepErrors.customerCountry = t('customerCountryRequired');
          }
          if (!formData.customerVisaType) {
            stepErrors.customerVisaType = t('customerVisaTypeRequired');
          }
        }
        break;
      case 7:
        // Notes optional
        break;
      default:
        break;
    }

    return stepErrors;
  };

  const isStepValid = (stepNumber) => {
    const stepErrors = validateStep(stepNumber);
    return Object.keys(stepErrors).length === 0;
  };

  const canProceed = () => isStepValid(currentStep);

  // Check if all required steps are valid for final submission
  const canSubmit = () => {
    for (let i = 1; i <= 6; i++) {
      if (!isStepValid(i)) return false;
    }
    return true;
  };

  const handleNext = () => {
    const stepErrors = validateStep(currentStep);

    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      setShowValidation(true);
      showWarning(t('fillAllFields'));
      return;
    }

    setErrors({});
    setShowValidation(false);

    if (currentStep < 7) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSaveDraft = async () => {
    setLoading(true);

    try {
      // Clean data before sending
      const cleanData = {
        ...formData,
        bankId: formData.bankId || null,
        branchId: formData.branchId || null,
        customerId: formData.customerId || null,
        isDraft: true
      };

      await loanAPI.saveDraft(cleanData);
      showSuccess(t('draftSavedNew'));

      // Clear sessionStorage after successful save
      sessionStorage.removeItem('loanFormData');
      sessionStorage.removeItem('loanFormCurrentStep');

      setTimeout(() => navigate('/loan/list'), 1500);
    } catch (error) {
      console.error('Failed to save draft:', error);
      showError(error.response?.data?.message || t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit()) {
      showWarning(t('checkInfo'));
      return;
    }

    showConfirm(
      t('confirmSend'),
      async () => {
        setLoading(true);

        try {
          // Clean data before sending
          const cleanData = {
            ...formData,
            branchId: formData.branchId || null,
            customerId: formData.customerId || null,
            isDraft: false
          };

          await loanAPI.create(cleanData);
          showSuccess(t('loanSentSuccess'));

          // Clear sessionStorage after successful submit
          sessionStorage.removeItem('loanFormData');
          sessionStorage.removeItem('loanFormCurrentStep');

          setTimeout(() => navigate('/loan/list'), 1500);
        } catch (error) {
          console.error('Failed to submit loan:', error);
          showError(error.response?.data?.message || t('errorOccurred'));
        } finally {
          setLoading(false);
        }
      }
    );
  };

  return (
    <div className="loan-form-page">
      <DialogComponent />
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/loan/list')}>
          <ArrowLeft size={20} />
        </button>
        <h1 className="page-title">{t('createNewLoanTitle')}</h1>
      </div>

      <div className="page-content">
        {/* Render all steps at once with validation indicators */}
        {steps.map((step, index) => {
          const StepComponent = step.component;
          const stepNumber = step.number;
          const isValid = isStepValid(stepNumber);
          const isCurrent = currentStep === stepNumber;

          // Check if all required steps (1-6) are valid
          const allRequiredStepsValid = [1, 2, 3, 4, 5, 6].every(n => isStepValid(n));

          // Hide Step 7 (Notes) until all required steps are complete
          if (stepNumber === 7 && !allRequiredStepsValid) {
            return null;
          }

          return (
            <div
              key={stepNumber}
              className={`step-section ${isCurrent ? 'step-active' : ''} ${isValid ? 'step-valid' : 'step-invalid'}`}
              id={`step-${stepNumber}`}
            >
              <div className="step-header">
                <div className="step-number-badge">
                  {isValid ? '✓' : stepNumber}
                </div>
                <h3 className="step-title">{step.title}</h3>
                {!isValid && stepNumber < currentStep && (
                  <span className="step-warning">⚠️ {t('incompleteStep')}</span>
                )}
              </div>
              <div className="step-content">
                <StepComponent
                  formData={formData}
                  setFormData={setFormData}
                  errors={showValidation && isCurrent ? errors : {}}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="form-actions-sticky">
        <div className="form-actions-container">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleSaveDraft}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-sm"></span>
                {t('savingDraft')}
              </>
            ) : (
              <>
                <Save size={18} />
                {t('saveDraft')}
              </>
            )}
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading || !canSubmit()}
          >
            {loading ? (
              <>
                <span className="spinner-sm"></span>
                {t('sending')}
              </>
            ) : (
              <>
                <Send size={18} />
                {t('sendLoan')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoanFormPage;
